/**
 * USE-WORK-ORDER-FORM.JS - Work Order Form State Management Hook
 * 
 * SUMMARY:
 * Custom hook that manages all form-related state for work order creation/editing.
 * Handles form fields, validation, input changes, and vendor selection.
 */

import { useState } from 'react';
import _ from 'lodash';
import { createWOFields } from "../../../../routes/contractors/data/wo";

/**
 * Custom hook for managing work order form state and validation.
 * 
 * @param {Array} vendorsList - List of available vendors
 * @param {string} projectId - Current project ID (if in project context)
 * @returns {Object} Form state and handlers
 * 
 * @example
 * const {
 *   fields,
 *   inputChangeHandler,
 *   vendorChangeHandler,
 *   validateForm
 * } = useWorkOrderForm(vendors, projectId);
 */
export const useWorkOrderForm = (vendorsList = [], projectId = null) => {
  const [fields, setFields] = useState(createWOFields);

  /**
   * Validates a single field based on its type and validation rules.
   * 
   * @param {Object} target - Input element (with type and value)
   * @param {Array} validation - Array of validation rules
   * @returns {Object} { error: string|false, valid: boolean }
   */
  const handleValidation = ({ type, value }, validation) => {
    let error = false;
    let valid = true;

    if (validation.includes('required') && _.isEmpty(value)) {
      return { error: 'This Field is Required', valid: false };
    }

    if (type === 'number') {
      if (isNaN(value)) {
        return { error: 'Please Enter a valid value', valid: false };
      }
      if (value < 0) {
        return { error: 'Value should be greater than 0', valid: false };
      }
    }

    return { error, valid };
  };

  /**
   * Handles input field changes with validation.
   * Updates field value, touched state, and validation status.
   * 
   * @param {Event} e - Input change event
   */
  const inputChangeHandler = (e) => {
    const updatedFields = { ...fields };
    updatedFields[e.target.name].value = e.target.value;
    updatedFields[e.target.name].touched = true;

    const { error, valid } = handleValidation(
      e.target,
      updatedFields[e.target.name].validation
    );

    updatedFields[e.target.name].error = error;
    updatedFields[e.target.name].valid = valid;

    setFields(updatedFields);
  };

  /**
   * Handles vendor selection and auto-fills address fields.
   * 
   * @param {string} value - Selected vendor ID
   */
  const vendorChangeHandler = (value) => {
    const vendor = vendorsList.find(item => item.id == value) || {};

    setFields(prev => ({
      ...prev,
      vendor: { ...prev.vendor, value: value, error: false, valid: true },
      street_address: { ...prev.street_address, value: vendor.street_address || '', error: false, valid: true },
      suburb: { ...prev.suburb, value: vendor.suburb || '', error: false, valid: true },
      state: { ...prev.state, value: vendor.state || '', error: false, valid: true },
      postcode: { ...prev.postcode, value: vendor.postcode || '', error: false, valid: true },
      country: { ...prev.country, value: vendor.country || '', error: false, valid: true },
    }));
  };

  /**
   * Generic field value change handler without validation.
   * 
   * @param {string} type - Field name
   * @param {any} value - New field value
   */
  const changeHandler = (type, value) => {
    setFields(prev => ({
      ...prev,
      [type]: { ...prev[type], value: value, error: false, valid: true },
    }));
  };

  /**
   * Validates all form fields before submission.
   * Marks all fields as touched and validates based on context (project or standalone).
   * 
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validateForm = () => {
    let isFormValid = true;
    const updatedFields = { ...fields };

    for (let key in updatedFields) {
      // If in project context, only validate project-specific fields
      if (projectId && updatedFields[key].isFromProject) {
        const { error, valid } = handleValidation(
          updatedFields[key],
          updatedFields[key].validation
        );
        updatedFields[key].touched = true;
        updatedFields[key].valid = valid;
        updatedFields[key].error = error;
        isFormValid = isFormValid && valid;
      } else if (!projectId) {
        // Validate all fields when not in project context
        const { error, valid } = handleValidation(
          updatedFields[key],
          updatedFields[key].validation
        );
        updatedFields[key].touched = true;
        updatedFields[key].valid = valid;
        updatedFields[key].error = error;
        isFormValid = isFormValid && valid;
      }
    }

    setFields(updatedFields);
    return isFormValid;
  };

  return {
    fields,
    setFields,
    inputChangeHandler,
    vendorChangeHandler,
    changeHandler,
    validateForm,
  };
};
